import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { useAuthContext } from '../../../features/auth/context/AuthContext';
import { clearAuth } from '../../../utils/authHelper';
import { ChevronDownIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import CookiePreferencesModal from './CookiePreferencesModal';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import {
  buildMidusPickerRows,
  resolveEffectiveDefaultModelId,
  patchDefaultChatModelKey,
  mergeUserFromPatch,
  persistChatInputModelSelection,
} from '../../../utils/defaultChatModel';
import darkLightImage from '../../../assets/dark-light.svg';
import lightImage from '../../../assets/light.svg';
import darkImage from '../../../assets/dark.svg';
import OpenDyslexicFont from '../../../assets/fonts/OpenDyslexic-Regular.otf';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const loadDyslexicFont = () => {
  return new Promise((resolve) => {
    if (process.env.NODE_ENV === 'production') {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = `${process.env.PUBLIC_URL || ''}/dyslexic-font.css`;
      fontLink.onload = () => resolve();
      fontLink.onerror = () => {
        const style = document.createElement('style');
        style.textContent = `
          @font-face {
            font-family: 'OpenDyslexic';
            src: url('${process.env.PUBLIC_URL || ''}/fonts/OpenDyslexic-Regular.otf') format('opentype');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }
        `;
        document.head.appendChild(style);
        resolve();
      };
      document.head.appendChild(fontLink);
    } else {
      const style = document.createElement('style');
      style.textContent = `
        @font-face {
          font-family: 'OpenDyslexic';
          src: url('${OpenDyslexicFont}') format('opentype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      `;
      document.head.appendChild(style);
      resolve();
    }
  });
};

/**
 * Settings tab content for AccountModal.
 * Appearance (color mode) and Fonts sections - same UI/logic as AppearanceSection.
 */
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
];

function SettingsTab({ onClose, user, setUser }) {
  const { darkMode, setDarkMode, resetToSystem } = useTheme();
  const [selectedColorMode, setSelectedColorMode] = useState(null);
  const [selectedFont, setSelectedFont] = useState(null);
  const [systemFont, setSystemFont] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fontLoaded, setFontLoaded] = useState(false);
  const [language, setLanguage] = useState('en');
  const [receiveExclusiveContent, setReceiveExclusiveContent] = useState(() => localStorage.getItem('settings-receiveExclusive') !== 'false');
  const [emailQueuedTask, setEmailQueuedTask] = useState(() => localStorage.getItem('settings-emailQueued') !== 'false');
  const [cookieModalOpen, setCookieModalOpen] = useState(false);
  const [chatModels, setChatModels] = useState([]);
  const [chatModelsLoading, setChatModelsLoading] = useState(true);
  const [defaultModelSavingId, setDefaultModelSavingId] = useState(null);

  const detectSystemFont = () => {
    const defaultFonts = window.getComputedStyle(document.body).getPropertyValue('font-family');
    return defaultFonts.split(',')[0].trim().replace(/['"]/g, '');
  };

  useEffect(() => {
    loadDyslexicFont().then(() => setFontLoaded(true));
  }, []);

  useEffect(() => {
    const loadChatModels = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setChatModelsLoading(false);
          return;
        }
        try {
          const res = await axios.get(`${API_BASE_URL}/admin/models`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          });
          if (Array.isArray(res.data) && res.data.length > 0) {
            setChatModels(res.data);
          }
        } catch {
          const fb = await axios.get(`${API_BASE_URL}/models/default`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          });
          if (Array.isArray(fb.data) && fb.data.length > 0) {
            setChatModels(fb.data);
          }
        }
      } catch (e) {
        console.error('SettingsTab: failed to load models', e);
      } finally {
        setChatModelsLoading(false);
      }
    };
    loadChatModels();
  }, []);

  const defaultModelRows = buildMidusPickerRows(chatModels);
  const effectiveDefaultModelId = resolveEffectiveDefaultModelId(
    defaultModelRows,
    user?.preferences?.defaultChatModelKey
  );

  const handleDefaultModelChange = async (e) => {
    const rowId = e.target.value;
    const row = defaultModelRows.find((r) => r.id === rowId);
    if (!setUser || defaultModelSavingId || !row || rowId === effectiveDefaultModelId) return;
    try {
      setDefaultModelSavingId(rowId);
      const data = await patchDefaultChatModelKey(rowId);
      const merged = mergeUserFromPatch(user, data);
      setUser(merged);
      persistChatInputModelSelection(row.provider, row.model);
    } catch (err) {
      console.error('SettingsTab: default model save failed', err);
    } finally {
      setDefaultModelSavingId(null);
    }
  };

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setSelectedColorMode('system');
          resetToSystem();
          setSelectedFont('default');
          setIsLoading(false);
          return;
        }
        const response = await axios.get(`${API_BASE_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        setSelectedColorMode(response.data?.preferences?.colorMode ?? 'system');
        setSelectedFont(response.data?.preferences?.fontPreference ?? 'default');
        setLanguage(response.data?.preferences?.language ?? 'en');
        if (!response.data?.preferences?.colorMode) resetToSystem();
      } catch (error) {
        setSelectedColorMode('system');
        resetToSystem();
        setSelectedFont('default');
      } finally {
        setIsLoading(false);
      }
    };
    loadPreferences();
  }, [resetToSystem]);

  useEffect(() => {
    setSystemFont(detectSystemFont());
  }, []);

  useEffect(() => {
    if (isLoading || selectedColorMode === null) return;
    switch (selectedColorMode) {
      case 'dark':
        setDarkMode(true);
        break;
      case 'light':
        setDarkMode(false);
        break;
      case 'system':
        resetToSystem();
        break;
    }
  }, [selectedColorMode, setDarkMode, resetToSystem, isLoading]);

  useEffect(() => {
    if (isLoading || selectedFont === null || !systemFont) return;
    const root = document.documentElement;
    switch (selectedFont) {
      case 'system':
        root.style.setProperty('--chat-font-family', systemFont);
        root.classList.remove('using-dyslexic-font');
        break;
      case 'dyslexic':
        root.style.setProperty('--chat-font-family', 'OpenDyslexic, system-ui, sans-serif');
        root.classList.add('using-dyslexic-font');
        break;
      default:
        root.style.setProperty('--chat-font-family', 'midus-sans, system-ui, sans-serif');
        root.classList.remove('using-dyslexic-font');
    }
  }, [selectedFont, systemFont, isLoading, fontLoaded]);

  const savePreferences = async (colorMode, font, lang) => {
    if (isUpdating) return;
    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');
      await axios.patch(
        `${API_BASE_URL}/users/me`,
        { preferences: { colorMode, fontPreference: font, language: lang ?? language } },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 10000 }
      );
    } catch (error) {
      if (error.response?.status !== 404 && error.response?.status !== 401 && error.response?.status !== 403) {
        setSelectedColorMode((p) => p);
        setSelectedFont((p) => p);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleColorModeChange = async (mode) => {
    setSelectedColorMode(mode);
    if (mode === 'system') resetToSystem();
    else setDarkMode(mode === 'dark');
    await savePreferences(mode, selectedFont, language);
  };

  const handleFontChange = (font) => {
    setSelectedFont(font);
    savePreferences(selectedColorMode, font, language);
  };

  const getColorModeClasses = (mode) => {
    const isSelected = selectedColorMode === mode;
    return `w-[8rem] flex-shrink-0 border ${isSelected ? 'border-[#999999] dark:border-[#737373] ring-1 ring-[#999999] dark:ring-[#737373]' : 'border-gray-200 dark:border-[#404040]'} px-2 pt-2 pb-2 cursor-pointer hover:border-[#404040] dark:hover:border-[#606060] focus:outline-none ${isSelected ? 'bg-gray-50 dark:bg-[#181818]' : ''}`;
  };

  const getFontClasses = (font) => {
    const isSelected = selectedFont === font;
    return `w-[8rem] flex-shrink-0 border ${isSelected ? 'border-[#999999] dark:border-[#737373] ring-1 ring-[#999999] dark:ring-[#737373]' : 'border-gray-200 dark:border-[#404040]'} px-2 pt-2 pb-2 cursor-pointer hover:border-[#404040] dark:hover:border-[#606060] focus:outline-none ${isSelected ? 'bg-gray-50 dark:bg-[#181818]' : ''}`;
  };

  const handleLanguageChange = async (e) => {
    const val = e.target.value;
    const prevVal = language;
    setLanguage(val);
    localStorage.setItem('settings-language', val);
    if (isUpdating) return;
    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      if (token) {
        await axios.patch(
          `${API_BASE_URL}/users/me`,
          { preferences: { language: val } },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 10000 }
        );
      }
    } catch (error) {
      if (error.response?.status !== 404 && error.response?.status !== 401 && error.response?.status !== 403) {
        setLanguage(prevVal);
        localStorage.setItem('settings-language', prevVal);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExclusiveContentToggle = () => {
    const next = !receiveExclusiveContent;
    setReceiveExclusiveContent(next);
    localStorage.setItem('settings-receiveExclusive', String(next));
  };

  const handleEmailQueuedToggle = () => {
    const next = !emailQueuedTask;
    setEmailQueuedTask(next);
    localStorage.setItem('settings-emailQueued', String(next));
  };

  const { setAuthTransitioning } = useAuthContext();

  const handleLogout = async () => {
    onClose?.();
    try {
      flushSync(() => setAuthTransitioning(true)); // Force sync re-render to show AuthLoader before clear
      clearAuth(); // Must clear BOTH localStorage AND sessionStorage - otherwise token persists and auth route redirects to /
      localStorage.clear(); // Clear rest of localStorage
      window.location.replace('/auth/onboarding');
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.replace('/auth/onboarding');
    }
  };

  const getPreviewFontStyle = (font) => {
    switch (font) {
      case 'system':
        return { fontFamily: systemFont };
      case 'dyslexic':
        return { fontFamily: 'OpenDyslexic, sans-serif' };
      default:
        return { fontFamily: 'midus-sans, system-ui, sans-serif' };
    }
  };

  const cardGridClass = 'flex flex-wrap gap-1.5';

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
      <div className="space-y-0">
        {/* Default model for new chats (parity with native header submenu + iOS Settings) */}
        {user && setUser ? (
        <section className="pb-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Default model</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Your model for new chats</p>
          {chatModelsLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading models…</p>
          ) : defaultModelRows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No models available.</p>
          ) : (
            <div className="relative w-full max-w-xs">
              <select
                id="settings-default-chat-model"
                value={effectiveDefaultModelId ?? defaultModelRows[0]?.id ?? ''}
                onChange={handleDefaultModelChange}
                disabled={Boolean(defaultModelSavingId)}
                aria-label="Default model for new chats"
                className="account-modal-input w-full appearance-none px-3 py-2 pr-9 border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#444] focus:border-gray-300 dark:focus:border-[#444] disabled:opacity-60"
              >
                {defaultModelRows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.displayName}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          )}
        </section>
        ) : null}

        {/* General */}
        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">General</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Language</label>
            <div className="relative w-full max-w-xs">
              <select
                value={language}
                onChange={handleLanguageChange}
                className="account-modal-input w-full appearance-none px-3 py-2 pr-9 border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#444] focus:border-gray-300 dark:focus:border-[#444]"
              >
                {LANGUAGES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </section>

        {/* Appearance */}
        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Appearance</h3>
          <div className={cardGridClass}>
            <div className={`account-modal-item account-modal-item-xl ${getColorModeClasses('light')} dark:hover:border-[#606060]`} onClick={() => handleColorModeChange('light')} role="button" tabIndex={0}>
              <div className="account-modal-appearance-img h-12 flex items-center justify-center mb-1.5 overflow-hidden">
                <img src={lightImage} alt="Light mode" className="account-modal-appearance-img w-full h-auto object-contain" />
              </div>
              <p className="text-center text-xs text-gray-900 dark:text-white whitespace-nowrap">Light</p>
            </div>
            <div className={`account-modal-item account-modal-item-xl ${getColorModeClasses('system')} dark:hover:border-[#606060]`} onClick={() => handleColorModeChange('system')} role="button" tabIndex={0}>
              <div className="account-modal-appearance-img h-12 flex items-center justify-center mb-1.5 overflow-hidden">
                <img src={darkLightImage} alt="Match system" className="account-modal-appearance-img w-full h-auto object-contain" />
              </div>
              <p className="text-center text-xs text-gray-900 dark:text-white whitespace-nowrap">Match System</p>
            </div>
            <div className={`account-modal-item account-modal-item-xl ${getColorModeClasses('dark')} dark:hover:border-[#606060]`} onClick={() => handleColorModeChange('dark')} role="button" tabIndex={0}>
              <div className="account-modal-appearance-img h-12 flex items-center justify-center mb-1.5 overflow-hidden">
                <img src={darkImage} alt="Dark mode" className="account-modal-appearance-img w-full h-auto object-contain" />
              </div>
              <p className="text-center text-xs text-gray-900 dark:text-white whitespace-nowrap">Dark</p>
            </div>
          </div>
        </section>

        {/* Fonts */}
        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Fonts</h3>
          <div className={cardGridClass}>
            <div className={`account-modal-item account-modal-item-xl ${getFontClasses('default')} dark:hover:border-[#606060]`} onClick={() => handleFontChange('default')} role="button" tabIndex={0}>
              <div className="h-8 flex items-center justify-center text-lg text-gray-900 dark:text-white" style={getPreviewFontStyle('default')}>Aa</div>
              <p className="text-center text-xs text-gray-900 dark:text-white whitespace-nowrap">Default</p>
            </div>
            <div className={`account-modal-item account-modal-item-xl ${getFontClasses('system')} dark:hover:border-[#606060]`} onClick={() => handleFontChange('system')} role="button" tabIndex={0}>
              <div className="h-8 flex items-center justify-center text-lg text-gray-900 dark:text-white" style={getPreviewFontStyle('system')}>Aa</div>
              <p className="text-center text-xs text-gray-900 dark:text-white whitespace-nowrap">System Font</p>
            </div>
            {/* <div className={`${getFontClasses('dyslexic')} dark:hover:border-[#606060]`} onClick={() => handleFontChange('dyslexic')} role="button" tabIndex={0}>
              <div className="h-8 flex items-center justify-center text-lg text-gray-900 dark:text-white" style={getPreviewFontStyle('dyslexic')}>Aa</div>
              <p className="text-center text-xs text-gray-900 dark:text-white whitespace-nowrap">Dyslexic friendly</p>
            </div> */}
          </div>
        </section>

        {/* Communication preferences */}
        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Communication preferences</h3>
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Receive exclusive content</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Get exclusive offers, event updates, excellent case examples and new feature guides.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={receiveExclusiveContent}
                onClick={handleExclusiveContentToggle}
                className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors cursor-pointer ${receiveExclusiveContent ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-[#444]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white dark:bg-[#1a1a1a] shadow-sm transition-transform ${receiveExclusiveContent ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Email me when my queued task starts</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">When enabled, we&apos;ll send you a timely email once your task finishes queuing and begins processing.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={emailQueuedTask}
                onClick={handleEmailQueuedToggle}
                className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors cursor-pointer ${emailQueuedTask ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-[#444]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white dark:bg-[#1a1a1a] shadow-sm transition-transform ${emailQueuedTask ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>
        </section>

        {/* Privacy & Cookies */}
        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <div className="flex items-center justify-between gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Privacy &amp; Cookies</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Manage analytics and view our privacy policy.</p>
            </div>
            <button
              type="button"
              onClick={() => setCookieModalOpen(true)}
              className="account-modal-btn px-4 py-2 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white text-sm font-medium hover:bg-gray-100 dark:hover:bg-[#252525]"
            >
              Manage
            </button>
          </div>
          <CookiePreferencesModal isOpen={cookieModalOpen} onClose={() => setCookieModalOpen(false)} />
        </section>

        {/* Export data */}
        <section className="py-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Data</h3>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Export data</span>
            <button
              type="button"
              disabled
              className="account-modal-btn px-4 py-2 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-100 dark:bg-[#252525] text-gray-400 dark:text-gray-500 text-sm font-medium cursor-not-allowed opacity-60"
            >
              Coming soon
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Download a copy of your data.</p>
        </section>

        {/* Log out */}
        <section className="pt-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Log out</span>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="account-modal-btn px-4 py-2 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-100 dark:hover:bg-red-950/50"
            >
              Log out
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SettingsTab;
